// M Media Corp — Portal login. Editorial, cream/ink.
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { usePortalAuth, formatApiError } from "./PortalAuth";

export default function PortalLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, user } = usePortalAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // If already signed in, bounce to the right home (clients → library, staff → dashboard)
  useEffect(() => {
    if (user) {
      const defaultDest = user.role === "client" ? "/library" : "/portal/dashboard";
      const dest = loc.state?.from?.pathname || defaultDest;
      nav(dest, { replace: true });
    }
  }, [user, loc, nav]);

  if (user) return null;

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome, ${data.name?.split(" ")[0] || "friend"}.`);
      const dest = data.role === "client" ? "/library" : "/portal/dashboard";
      nav(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream text-ink flex" data-testid="portal-login-page">
      {/* LEFT — editorial mark */}
      <aside className="hidden md:flex w-2/5 lg:w-1/2 bg-ink text-cream relative overflow-hidden">
        <video
          autoPlay muted loop playsInline preload="auto"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        >
          <source src="/hero-kling.webm" type="video/webm" />
          <source src="/hero-kling-r.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-ink/60" />
        <div className="relative z-10 flex flex-col justify-between p-10 lg:p-16 w-full">
          <Link to="/" className="block">
            <img src="/brand/mmedia-logo-light.png" alt="M Media Corp" className="h-14 lg:h-16 w-auto opacity-95 hover:opacity-100 transition-opacity" draggable="false" />
          </Link>
          <div>
            <div className="overline text-cream/65 mb-4">Members&apos; Entrance</div>
            <h1 className="font-display uppercase text-5xl lg:text-7xl leading-[0.85]">
              The room <br/>
              <span className="serif-italic font-serif normal-case">behind the room</span>.
            </h1>
            <p className="font-serif italic text-lg lg:text-xl mt-8 max-w-md text-cream/85 leading-snug">
              Private workspace for M Media clients and the editors who build their stories.
            </p>
          </div>
          <div className="overline text-cream/50">Issue 26 · Newport Beach</div>
        </div>
      </aside>

      {/* RIGHT — form */}
      <main className="flex-1 flex items-center justify-center px-6 md:px-16 py-14">
        <div className="w-full max-w-md">
          <div className="overline text-ink/55">Portal · Sign in</div>
          <h2 className="font-display uppercase text-5xl md:text-6xl leading-[0.85] mt-3">
            Good to see you.
          </h2>
          <p className="font-serif italic text-ink/70 mt-4">
            One door for clients and the team. Sign in with the email we have on file.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-7" data-testid="portal-login-form">
            <div>
              <label className="overline text-ink/55" htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent text-ink py-3 outline-none border-0 border-b border-ink/30 focus:border-ink transition-colors mt-1"
                data-testid="portal-email-input"
              />
            </div>
            <div>
              <label className="overline text-ink/55" htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent text-ink py-3 outline-none border-0 border-b border-ink/30 focus:border-ink transition-colors mt-1"
                data-testid="portal-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="btn-ink w-full justify-between disabled:opacity-60"
              data-testid="portal-login-submit"
            >
              <span>{busy ? "Signing in…" : "Sign in"}</span>
              <ArrowUpRight size={16} />
            </button>
          </form>

          <p className="text-ink/55 text-sm mt-10 leading-relaxed">
            Forgot your password? Email <a className="underline" href="mailto:info@mmediacorp.com">info@mmediacorp.com</a> and the desk will reset it for you.
          </p>
        </div>
      </main>
    </div>
  );
}
